import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { OnboardingProfile } from '@/types/onboarding';

const ONBOARDING_STORAGE_KEY = 'utm_auditor_onboarding_completed';

interface OnboardingStatus {
  completed: boolean;
  surveyCompleted: boolean;
  onboardingProfile: OnboardingProfile;
}

const getEmptyProfile = (): OnboardingProfile => ({});

export const useOnboardingStatus = () => {
  const { user } = useAuth();

  return useQuery<OnboardingStatus>({
    queryKey: ['onboarding-status', user?.id],
    queryFn: async () => {
      if (!user) {
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
        return { completed: false, surveyCompleted: false, onboardingProfile: getEmptyProfile() };
      }

      const { data: userRecord, error: userError } = await supabase
        .from('users')
        .select('onboarding_profile, survey_completed_at')
        .eq('id', user.id)
        .maybeSingle();

      if (userError) throw userError;

      const onboardingProfile = (userRecord?.onboarding_profile as OnboardingProfile | null) ?? getEmptyProfile();
      const surveyCompleted = Boolean(userRecord?.survey_completed_at);

      const { data: userWorkspaces, error: workspaceError } = await supabase
        .from('workspace_users')
        .select('workspace_id')
        .eq('user_id', user.id);

      if (workspaceError) throw workspaceError;

      if (!userWorkspaces || userWorkspaces.length === 0) {
        localStorage.removeItem(ONBOARDING_STORAGE_KEY);
        return { completed: false, surveyCompleted, onboardingProfile };
      }

      const workspaceIds = userWorkspaces.map((w) => w.workspace_id);
      const { data: dataSources, error: dataSourceError } = await supabase
        .from('data_sources')
        .select('id')
        .in('workspace_id', workspaceIds)
        .limit(1);

      if (dataSourceError) throw dataSourceError;

      const hasDataSources = Boolean(dataSources && dataSources.length > 0);

      if (hasDataSources) {
        localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
        return { completed: true, surveyCompleted, onboardingProfile };
      }

      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      return { completed: false, surveyCompleted, onboardingProfile };
    },
    enabled: !!user,
  });
};

export const useSaveOnboardingSurvey = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profile: OnboardingProfile | null) => {
      if (!user) throw new Error('You must be logged in to update onboarding info.');

      const payload = {
        onboarding_profile: profile ?? getEmptyProfile(),
        survey_completed_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('users' as any)
        .update(payload)
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
      queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    },
  });
};
